import type { ChannelRollup } from "@/lib/channelReconciliation";
import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export interface GrowthSourcesRow {
  name: string;
  channelId: string | null;
  channelName: string;
  leads: number;
  appointments: number;
  jobs: number;
  wonNet: number;
  revenue: number;
  intakeTotal: number;
  intakeApproved: number;
  duplicates: number;
  outOfArea: number;
  issues: number;
  leadCost: number;
  validPct: number;
  dupePct: number;
  netConvPct: number;
  sourceType: string;
}

export interface GrowthAttributionSourceRow {
  source: string;
  channelId: string;
  channelName: string;
  leads: number;
  jobs: number;
  revenue: number;
  issues: number;
  netJobs: number;
  leadCost: number;
  cpaNet: number;
  netConversion: number;
  leadQuality: number;
}

export interface GrowthAttributionJobRow {
  jobId: string;
  jobNumber: string;
  customerName: string;
  currentStage: string;
  branch: string;
  region: string;
  salesRep: string;
  leadSource: string;
  channelId: string;
  channelName: string;
  campaignName: string;
  callCenterRep: string;
  appointmentTime: string;
  leadCost: number;
  jobValue: number;
  issueCount: number;
  leadQuality: "Valid" | "Out-of-Area" | "Low Quality";
  bookingScore: number;
  outcome: "Net" | "Pending" | "Canceled" | "Finance Declined" | "—";
}

export interface GrowthCampaignRow {
  campaign: string;
  jobs: number;
  revenue: number;
  leadCost: number;
  netJobs: number;
  netConversion: number;
  cpa: number;
  sources: string;
  channel: string;
}

export interface GrowthCallCenterRepRow {
  rep: string;
  appointments: number;
  closed: number;
  revenue: number;
  highFitClosed: number;
  highFitTotal: number;
  leadSpend: number;
  noShows: number;
  speedSec: number;
  showRate: number;
  closeRate: number;
  highFitConversion: number;
  costPerAppt: number;
  revenuePerAppt: number;
}

export interface GrowthMarketingSummary {
  jobsAttributed: number;
  leadSources: number;
  attributedRevenue: number;
  leadSpend: number;
  netJobs: number;
  closedCount: number;
  noShows: number;
  roas: number;
  cpaNet: number;
  avgDealSize: number;
  costPerAppt: number;
  revenuePerAppt: number;
  showRate: number;
  closeRate: number;
  avgSpeedSec: number;
}

export interface GrowthDispositionRow {
  name: string;
  sourceType: string;
  totalLeads: number;
  contacted: number;
  noContact: number;
  appointmentsSet: number;
  appointmentsCompleted: number;
  noShow: number;
  sold: number;
  rejected: number;
  rejectionReasons: Record<string, number>;
  revenue: number;
  cost: number;
  issueCount: number;
  creditAmount: number;
  conversionRate: number;
}

export interface GrowthAttributionJobsPageQuery {
  page: number;
  pageSize: number;
  search?: string;
  source?: string | null;
  channel?: string | null;
  status?: string | null;
  quality?: string | null;
  branch?: string | null;
  region?: string | null;
  sortKey?: string;
  sortDir?: "asc" | "desc";
  refreshToken?: number;
}

export interface GrowthDispositionQuery {
  sourceType?: string;
  dateFrom?: string;
  dateTo?: string;
  refreshToken?: number;
}

type ChannelRollupRow = {
  id?: string | null;
  name?: string | null;
  type?: ChannelRollup["type"] | null;
  sources?: string[] | null;
  active?: boolean | null;
  leads?: number | null;
  appointments?: number | null;
  won_gross?: number | null;
  won_net?: number | null;
  pending?: number | null;
  valid_lead_pct?: number | null;
  out_of_area_pct?: number | null;
  intake_count?: number | null;
  monthly_spend?: number | null;
  revenue?: number | null;
  cpl?: number | null;
  cpb?: number | null;
  cpa_gross?: number | null;
  cpa_net?: number | null;
  net_conversion_pct?: number | null;
  roi?: number | null;
};

type SourcesViewRow = {
  name?: string | null;
  source_type?: string | null;
  channel_id?: string | null;
  channel_name?: string | null;
  leads?: number | null;
  appointments?: number | null;
  jobs?: number | null;
  won_net?: number | null;
  revenue?: number | null;
  intake_total?: number | null;
  intake_approved?: number | null;
  duplicates?: number | null;
  out_of_area?: number | null;
  issues?: number | null;
  lead_cost?: number | null;
  valid_pct?: number | null;
  dupe_pct?: number | null;
  net_conv_pct?: number | null;
};

type AttributionSourceViewRow = {
  source?: string | null;
  channel_id?: string | null;
  channel_name?: string | null;
  leads?: number | null;
  jobs?: number | null;
  revenue?: number | null;
  issues?: number | null;
  net_jobs?: number | null;
  lead_cost?: number | null;
  cpa_net?: number | null;
  net_conversion?: number | null;
  lead_quality?: number | null;
};

type AttributionJobViewRow = {
  job_id?: string | null;
  job_number?: string | null;
  customer_name?: string | null;
  current_stage?: string | null;
  branch?: string | null;
  region?: string | null;
  sales_rep?: string | null;
  lead_source?: string | null;
  channel_id?: string | null;
  channel_name?: string | null;
  campaign_name?: string | null;
  call_center_rep?: string | null;
  appointment_time?: string | null;
  lead_cost?: number | null;
  job_value?: number | null;
  issue_count?: number | null;
  lead_quality?: GrowthAttributionJobRow["leadQuality"] | null;
  booking_score?: number | null;
  outcome?: GrowthAttributionJobRow["outcome"] | null;
};

type CampaignViewRow = {
  campaign?: string | null;
  jobs?: number | null;
  revenue?: number | null;
  lead_cost?: number | null;
  net_jobs?: number | null;
  net_conversion?: number | null;
  cpa?: number | null;
  sources?: string | null;
  channel?: string | null;
};

type CallCenterRepViewRow = {
  rep?: string | null;
  appointments?: number | null;
  closed?: number | null;
  revenue?: number | null;
  high_fit_closed?: number | null;
  high_fit_total?: number | null;
  lead_spend?: number | null;
  no_shows?: number | null;
  speed_sec?: number | null;
  show_rate?: number | null;
  close_rate?: number | null;
  high_fit_conversion?: number | null;
  cost_per_appt?: number | null;
  revenue_per_appt?: number | null;
};

type MarketingSummaryViewRow = {
  jobs_attributed?: number | null;
  lead_sources?: number | null;
  attributed_revenue?: number | null;
  lead_spend?: number | null;
  net_jobs?: number | null;
  closed_count?: number | null;
  no_shows?: number | null;
  roas?: number | null;
  cpa_net?: number | null;
  avg_deal_size?: number | null;
  cost_per_appt?: number | null;
  revenue_per_appt?: number | null;
  show_rate?: number | null;
  close_rate?: number | null;
  avg_speed_sec?: number | null;
};

type DispositionViewRow = {
  name?: string | null;
  source_type?: string | null;
  total_leads?: number | null;
  contacted?: number | null;
  no_contact?: number | null;
  appointments_set?: number | null;
  appointments_completed?: number | null;
  no_show?: number | null;
  sold?: number | null;
  rejected?: number | null;
  rejection_reasons?: Record<string, number> | null;
  revenue?: number | null;
  cost?: number | null;
  issue_count?: number | null;
  credit_amount?: number | null;
  conversion_rate?: number | null;
};

const CHANNEL_ROLLUP_COLUMNS = [
  "id",
  "name",
  "type",
  "sources",
  "active",
  "leads",
  "appointments",
  "won_gross",
  "won_net",
  "pending",
  "valid_lead_pct",
  "out_of_area_pct",
  "intake_count",
  "monthly_spend",
  "revenue",
  "cpl",
  "cpb",
  "cpa_gross",
  "cpa_net",
  "net_conversion_pct",
  "roi",
  "sort_order",
].join(", ");

const SOURCE_COLUMNS = [
  "name",
  "source_type",
  "channel_id",
  "channel_name",
  "leads",
  "appointments",
  "jobs",
  "won_net",
  "revenue",
  "intake_total",
  "intake_approved",
  "duplicates",
  "out_of_area",
  "issues",
  "lead_cost",
  "valid_pct",
  "dupe_pct",
  "net_conv_pct",
  "sort_at",
].join(", ");

const ATTRIBUTION_SOURCE_COLUMNS = [
  "source",
  "channel_id",
  "channel_name",
  "leads",
  "jobs",
  "revenue",
  "issues",
  "net_jobs",
  "lead_cost",
  "cpa_net",
  "net_conversion",
  "lead_quality",
  "sort_at",
].join(", ");

const ATTRIBUTION_JOB_COLUMNS = [
  "job_id",
  "job_number",
  "customer_name",
  "current_stage",
  "branch",
  "region",
  "sales_rep",
  "lead_source",
  "channel_id",
  "channel_name",
  "campaign_name",
  "call_center_rep",
  "appointment_time",
  "lead_cost",
  "job_value",
  "issue_count",
  "lead_quality",
  "booking_score",
  "outcome",
  "sort_at",
].join(", ");

const CAMPAIGN_COLUMNS = ["campaign", "jobs", "revenue", "lead_cost", "net_jobs", "net_conversion", "cpa", "sources", "channel", "sort_at"].join(", ");
const CALL_CENTER_COLUMNS = ["rep", "appointments", "closed", "revenue", "high_fit_closed", "high_fit_total", "lead_spend", "no_shows", "speed_sec", "show_rate", "close_rate", "high_fit_conversion", "cost_per_appt", "revenue_per_appt", "sort_at"].join(", ");
const SUMMARY_COLUMNS = ["jobs_attributed", "lead_sources", "attributed_revenue", "lead_spend", "net_jobs", "closed_count", "no_shows", "roas", "cpa_net", "avg_deal_size", "cost_per_appt", "revenue_per_appt", "show_rate", "close_rate", "avg_speed_sec"].join(", ");
const DISPOSITION_COLUMNS = ["name", "source_type", "total_leads", "contacted", "no_contact", "appointments_set", "appointments_completed", "no_show", "sold", "rejected", "rejection_reasons", "revenue", "cost", "issue_count", "credit_amount", "conversion_rate", "sort_at"].join(", ");

const EMPTY_SUMMARY: GrowthMarketingSummary = {
  jobsAttributed: 0,
  leadSources: 0,
  attributedRevenue: 0,
  leadSpend: 0,
  netJobs: 0,
  closedCount: 0,
  noShows: 0,
  roas: 0,
  cpaNet: 0,
  avgDealSize: 0,
  costPerAppt: 0,
  revenuePerAppt: 0,
  showRate: 0,
  closeRate: 0,
  avgSpeedSec: 0,
};

function asString(value: unknown, fallback = ""): string {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function clampPage(query: { page: number; pageSize: number }) {
  const page = Math.max(0, Number(query.page) || 0);
  const pageSize = Math.max(1, Math.min(Number(query.pageSize) || 25, 5000));
  return { page, pageSize, from: page * pageSize, to: page * pageSize + pageSize - 1 };
}

function sanitizeLike(value: string): string {
  return value.replace(/[%_]/g, "").replace(/,/g, " ").trim();
}

function recordPageMetric(key: string, startedAt: number, payload: unknown) {
  let payloadBytes = 0;
  try {
    payloadBytes = JSON.stringify(payload ?? null).length;
  } catch {
    payloadBytes = 0;
  }
  recordQueryMetric({ key, durationMs: Date.now() - startedAt, payloadBytes, cacheHit: false });
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  observeQueryStart(key);
  const startedAt = Date.now();
  try {
    const result = await run();
    recordPageMetric(key, startedAt, result);
    return result;
  } catch (error) {
    recordPageMetric(key, startedAt, { error: String(error) });
    throw error;
  }
}

function mapChannelRollup(row: ChannelRollupRow): ChannelRollup {
  return {
    id: asString(row.id, "unattributed"),
    name: asString(row.name, "Unattributed"),
    type: (asString(row.type, "derived") as ChannelRollup["type"]),
    sources: Array.isArray(row.sources) ? row.sources : [],
    active: row.active ?? true,
    leads: asNumber(row.leads),
    appointments: asNumber(row.appointments),
    wonGross: asNumber(row.won_gross),
    wonNet: asNumber(row.won_net),
    pending: asNumber(row.pending),
    validLeadPct: asNumber(row.valid_lead_pct),
    outOfAreaPct: asNumber(row.out_of_area_pct),
    intakeCount: asNumber(row.intake_count),
    monthlySpend: asNumber(row.monthly_spend),
    revenue: asNumber(row.revenue),
    cpl: asNumber(row.cpl),
    cpb: asNumber(row.cpb),
    cpaGross: asNumber(row.cpa_gross),
    cpaNet: asNumber(row.cpa_net),
    netConversionPct: asNumber(row.net_conversion_pct),
    roi: asNumber(row.roi),
  };
}

function mapSource(row: SourcesViewRow): GrowthSourcesRow {
  return {
    name: asString(row.name, "Unattributed"),
    channelId: asString(row.channel_id) || null,
    channelName: asString(row.channel_name, "Unattributed"),
    sourceType: asString(row.source_type, "unknown"),
    leads: asNumber(row.leads),
    appointments: asNumber(row.appointments),
    jobs: asNumber(row.jobs),
    wonNet: asNumber(row.won_net),
    revenue: asNumber(row.revenue),
    intakeTotal: asNumber(row.intake_total),
    intakeApproved: asNumber(row.intake_approved),
    duplicates: asNumber(row.duplicates),
    outOfArea: asNumber(row.out_of_area),
    issues: asNumber(row.issues),
    leadCost: asNumber(row.lead_cost),
    validPct: asNumber(row.valid_pct),
    dupePct: asNumber(row.dupe_pct),
    netConvPct: asNumber(row.net_conv_pct),
  };
}

function mapAttributionSource(row: AttributionSourceViewRow): GrowthAttributionSourceRow {
  return {
    source: asString(row.source, "Unattributed"),
    channelId: asString(row.channel_id, "unattributed"),
    channelName: asString(row.channel_name, "Unattributed"),
    leads: asNumber(row.leads),
    jobs: asNumber(row.jobs),
    revenue: asNumber(row.revenue),
    issues: asNumber(row.issues),
    netJobs: asNumber(row.net_jobs),
    leadCost: asNumber(row.lead_cost),
    cpaNet: asNumber(row.cpa_net),
    netConversion: asNumber(row.net_conversion),
    leadQuality: asNumber(row.lead_quality, 100),
  };
}

function mapAttributionJob(row: AttributionJobViewRow): GrowthAttributionJobRow {
  return {
    jobId: asString(row.job_id),
    jobNumber: asString(row.job_number),
    customerName: asString(row.customer_name, "Unknown"),
    currentStage: asString(row.current_stage, "Sale Closed"),
    branch: asString(row.branch),
    region: asString(row.region),
    salesRep: asString(row.sales_rep),
    leadSource: asString(row.lead_source, "Unattributed"),
    channelId: asString(row.channel_id, "unattributed"),
    channelName: asString(row.channel_name, "Unattributed"),
    campaignName: asString(row.campaign_name, "Unspecified"),
    callCenterRep: asString(row.call_center_rep, "Unassigned"),
    appointmentTime: asString(row.appointment_time),
    leadCost: asNumber(row.lead_cost),
    jobValue: asNumber(row.job_value),
    issueCount: asNumber(row.issue_count),
    leadQuality: row.lead_quality ?? "Valid",
    bookingScore: asNumber(row.booking_score),
    outcome: row.outcome ?? "—",
  };
}

function mapCampaign(row: CampaignViewRow): GrowthCampaignRow {
  return {
    campaign: asString(row.campaign, "Unspecified"),
    jobs: asNumber(row.jobs),
    revenue: asNumber(row.revenue),
    leadCost: asNumber(row.lead_cost),
    netJobs: asNumber(row.net_jobs),
    netConversion: asNumber(row.net_conversion),
    cpa: asNumber(row.cpa),
    sources: asString(row.sources),
    channel: asString(row.channel),
  };
}

function mapCallCenterRep(row: CallCenterRepViewRow): GrowthCallCenterRepRow {
  return {
    rep: asString(row.rep, "Unassigned"),
    appointments: asNumber(row.appointments),
    closed: asNumber(row.closed),
    revenue: asNumber(row.revenue),
    highFitClosed: asNumber(row.high_fit_closed),
    highFitTotal: asNumber(row.high_fit_total),
    leadSpend: asNumber(row.lead_spend),
    noShows: asNumber(row.no_shows),
    speedSec: asNumber(row.speed_sec),
    showRate: asNumber(row.show_rate),
    closeRate: asNumber(row.close_rate),
    highFitConversion: asNumber(row.high_fit_conversion),
    costPerAppt: asNumber(row.cost_per_appt),
    revenuePerAppt: asNumber(row.revenue_per_appt),
  };
}

function mapMarketingSummary(row: MarketingSummaryViewRow | null | undefined): GrowthMarketingSummary {
  if (!row) return EMPTY_SUMMARY;
  return {
    jobsAttributed: asNumber(row.jobs_attributed),
    leadSources: asNumber(row.lead_sources),
    attributedRevenue: asNumber(row.attributed_revenue),
    leadSpend: asNumber(row.lead_spend),
    netJobs: asNumber(row.net_jobs),
    closedCount: asNumber(row.closed_count),
    noShows: asNumber(row.no_shows),
    roas: asNumber(row.roas),
    cpaNet: asNumber(row.cpa_net),
    avgDealSize: asNumber(row.avg_deal_size),
    costPerAppt: asNumber(row.cost_per_appt),
    revenuePerAppt: asNumber(row.revenue_per_appt),
    showRate: asNumber(row.show_rate),
    closeRate: asNumber(row.close_rate),
    avgSpeedSec: asNumber(row.avg_speed_sec),
  };
}

function mapDisposition(row: DispositionViewRow): GrowthDispositionRow {
  return {
    name: asString(row.name, "Unattributed"),
    sourceType: asString(row.source_type, "unknown"),
    totalLeads: asNumber(row.total_leads),
    contacted: asNumber(row.contacted),
    noContact: asNumber(row.no_contact),
    appointmentsSet: asNumber(row.appointments_set),
    appointmentsCompleted: asNumber(row.appointments_completed),
    noShow: asNumber(row.no_show),
    sold: asNumber(row.sold),
    rejected: asNumber(row.rejected),
    rejectionReasons: row.rejection_reasons ?? {},
    revenue: asNumber(row.revenue),
    cost: asNumber(row.cost),
    issueCount: asNumber(row.issue_count),
    creditAmount: asNumber(row.credit_amount),
    conversionRate: asNumber(row.conversion_rate),
  };
}

function applyJobFilters(query: any, input: GrowthAttributionJobsPageQuery) {
  let next = query;
  const source = asString(input.source ?? "");
  if (source && source !== "all") next = next.eq("lead_source", source);
  const channel = asString(input.channel ?? "");
  if (channel && channel !== "all") next = next.eq("channel_name", channel);
  const status = asString(input.status ?? "");
  if (status && status !== "all") next = next.eq("outcome", status);
  const quality = asString(input.quality ?? "");
  if (quality && quality !== "all") next = next.eq("lead_quality", quality);
  const branch = asString(input.branch ?? "");
  if (branch && branch !== "all") next = next.eq("branch", branch);
  const region = asString(input.region ?? "");
  if (region && region !== "all") next = next.eq("region", region);

  const term = sanitizeLike(String(input.search ?? ""));
  if (term) {
    next = next.or([
      "job_id",
      "job_number",
      "customer_name",
      "lead_source",
      "channel_name",
      "campaign_name",
      "call_center_rep",
    ].map((column) => `${column}.ilike.%${term}%`).join(","));
  }

  return next;
}

const JOB_SORT_COLUMNS: Record<string, string> = {
  id: "job_number",
  customer: "customer_name",
  source: "lead_source",
  channel: "channel_name",
  score: "booking_score",
  apptDate: "appointment_time",
  value: "job_value",
};

export const GrowthChannelsPageService = {
  async getChannelRollups(refreshToken = 0): Promise<{ data: ChannelRollup[]; error: unknown | null }> {
    const key = `growth.channels.${refreshToken}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_growth_channel_rollups_ui")
          .select(CHANNEL_ROLLUP_COLUMNS)
          .order("sort_order", { ascending: true });
        if (error) throw error;
        return { data: ((data ?? []) as ChannelRollupRow[]).map(mapChannelRollup), error: null };
      });
    } catch (error) {
      console.warn("GrowthChannelsPageService.getChannelRollups failed:", error);
      return { data: [], error };
    }
  },

  async getSources(refreshToken = 0): Promise<{ data: GrowthSourcesRow[]; error: unknown | null }> {
    const key = `growth.sources.${refreshToken}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_growth_sources_ui")
          .select(SOURCE_COLUMNS)
          .order("leads", { ascending: false });
        if (error) throw error;
        return { data: ((data ?? []) as SourcesViewRow[]).map(mapSource), error: null };
      });
    } catch (error) {
      console.warn("GrowthChannelsPageService.getSources failed:", error);
      return { data: [], error };
    }
  },

  async getAttributionSources(refreshToken = 0): Promise<{ data: GrowthAttributionSourceRow[]; error: unknown | null }> {
    const key = `growth.attribution-sources.${refreshToken}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_growth_attribution_sources_ui")
          .select(ATTRIBUTION_SOURCE_COLUMNS)
          .order("revenue", { ascending: false });
        if (error) throw error;
        return { data: ((data ?? []) as AttributionSourceViewRow[]).map(mapAttributionSource), error: null };
      });
    } catch (error) {
      console.warn("GrowthChannelsPageService.getAttributionSources failed:", error);
      return { data: [], error };
    }
  },

  async getAttributionJobsPage(query: GrowthAttributionJobsPageQuery): Promise<PageResponse<GrowthAttributionJobRow>> {
    const { from, to } = clampPage(query);
    const key = `growth.attribution-jobs.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const sortColumn = JOB_SORT_COLUMNS[query.sortKey ?? "value"] ?? "job_value";
        let request = supabase
          .from("v_growth_attribution_jobs_ui")
          .select(ATTRIBUTION_JOB_COLUMNS, { count: "exact" })
          .order(sortColumn, { ascending: query.sortDir === "asc", nullsFirst: false })
          .range(from, to);
        request = applyJobFilters(request, query);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as AttributionJobViewRow[]).map(mapAttributionJob), count: count ?? 0, error: null };
      });
    } catch (error) {
      console.warn("GrowthChannelsPageService.getAttributionJobsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getCampaigns(refreshToken = 0): Promise<{ data: GrowthCampaignRow[]; error: unknown | null }> {
    const key = `growth.campaigns.${refreshToken}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_growth_campaigns_ui")
          .select(CAMPAIGN_COLUMNS)
          .order("revenue", { ascending: false });
        if (error) throw error;
        return { data: ((data ?? []) as CampaignViewRow[]).map(mapCampaign), error: null };
      });
    } catch (error) {
      console.warn("GrowthChannelsPageService.getCampaigns failed:", error);
      return { data: [], error };
    }
  },

  async getCallCenterReps(refreshToken = 0): Promise<{ data: GrowthCallCenterRepRow[]; error: unknown | null }> {
    const key = `growth.callcenter.${refreshToken}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_growth_callcenter_reps_ui")
          .select(CALL_CENTER_COLUMNS)
          .order("revenue", { ascending: false });
        if (error) throw error;
        return { data: ((data ?? []) as CallCenterRepViewRow[]).map(mapCallCenterRep), error: null };
      });
    } catch (error) {
      console.warn("GrowthChannelsPageService.getCallCenterReps failed:", error);
      return { data: [], error };
    }
  },

  async getMarketingSummary(refreshToken = 0): Promise<{ data: GrowthMarketingSummary; error: unknown | null }> {
    const key = `growth.summary.${refreshToken}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_growth_marketing_summary")
          .select(SUMMARY_COLUMNS)
          .maybeSingle();
        if (error) throw error;
        return { data: mapMarketingSummary(data as MarketingSummaryViewRow | null), error: null };
      });
    } catch (error) {
      console.warn("GrowthChannelsPageService.getMarketingSummary failed:", error);
      return { data: EMPTY_SUMMARY, error };
    }
  },

  async getDispositionSources(query: GrowthDispositionQuery = {}): Promise<{ data: GrowthDispositionRow[]; error: unknown | null }> {
    const key = `growth.dispositions.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        let request = supabase
          .from("v_growth_disposition_sources_ui")
          .select(DISPOSITION_COLUMNS)
          .order("total_leads", { ascending: false });
        const sourceType = asString(query.sourceType);
        if (sourceType && sourceType !== "all") request = request.eq("source_type", sourceType);
        if (query.dateFrom) request = request.gte("sort_at", query.dateFrom);
        if (query.dateTo) request = request.lte("sort_at", `${query.dateTo}T23:59:59.999Z`);
        const { data, error } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as DispositionViewRow[]).map(mapDisposition), error: null };
      });
    } catch (error) {
      console.warn("GrowthChannelsPageService.getDispositionSources failed:", error);
      return { data: [], error };
    }
  },
};
